/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

export interface ChecklistTask {
  label: string;
  description?: string;
  done: boolean;
  // A tester app link or an in-app ui-router state; absent for a task with nowhere to go.
  href?: string;
  state?: string;
  params?: Record<string, unknown>;
  // Done tasks without a follow-up show as plain text instead of a link.
  staticWhenDone?: boolean;
}

// The New Joiner Checklist card: the tasks a tester should finish in their first 30 days.
@Component({
  selector: 'ut-dashboard-checklist',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './dashboard-checklist.component.html',
  styleUrls: ['./dashboard-checklist.component.scss'],
})
export class DashboardChecklistComponent {
  @Input({ required: true }) user: any;
  @Input() testerAppUrl = '';

  get tasks(): ChecklistTask[] {
    const profileUrl = `${this.testerAppUrl}/account/my-profile/`;
    return [
      { label: 'Create your Account: Welcome to uTest!', done: true, staticWhenDone: true },
      {
        label: 'Confirm your Email Address',
        description: 'Click the link in your email as the final step to create your uTest account.',
        done: !!this.user.has_confirmed_email,
        href: profileUrl,
        staticWhenDone: true,
      },
      {
        label: 'Setup your Payment Method',
        description: 'Enable yourself to get paid.',
        done: !!this.user.has_payment_method,
        href: `${this.testerAppUrl}/account/payments`,
      },
      {
        label: 'Connect with the Community',
        description: 'Share experiences and stay up to date.',
        done: !!this.user.has_viewed_community_page,
        state: 'community',
      },
      {
        label: 'Complete your Profile',
        description: 'Make yourself available to participate in Projects that match your expertise.',
        done: !!this.user.has_completed_profile,
        href: profileUrl,
      },
      {
        label: 'Scan the Projects Board',
        description: 'Find test opportunities that match your skills and interests.',
        done: !!this.user.has_applied_to_project,
        state: 'project',
        params: { tab: 'suggested' },
      },
      {
        label: 'Take a course at the Academy',
        description: 'Learn best practices and how uTest works.',
        done: !!this.user.has_passed_academy,
        state: 'academy-home',
      },
    ];
  }
}
